import React, { useEffect } from "react";
import tw from "tailwind-styled-components";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { toast } from "react-toastify";
import { Select, MenuItem } from "@mui/material";
import { FaRupeeSign, FaRegCalendar } from "react-icons/fa";

const AdvanceRent = ({
  initialValues = {},
  setIsAddDuesModal,
  setSelectedOption,
}) => {
  const today = moment().format("YYYY-MM-DD");

  const formik = useFormik({
    initialValues: {
      amount: initialValues.amount || "",
      date: initialValues.date
        ? moment(initialValues.date).format("YYYY-MM-DD")
        : today, // default today
      dueDate: initialValues.dueDate
        ? moment(initialValues.dueDate).format("YYYY-MM-DD")
        : "",
      endDate: initialValues.endDate
        ? moment(initialValues.endDate).format("YYYY-MM-DD")
        : "",
      description: initialValues.description || "",
      rentDuration: initialValues.rentDuration || "",
    },
    validationSchema: Yup.object({
      amount: Yup.number(),
      date: Yup.date(),
      dueDate: Yup.date(),
      description: Yup.string(),
      rentDuration: Yup.string(),
    }),
    onSubmit: (values) => {
      if (!values.amount) {
        toast.error("Please enter amount");
        return;
      }
      if (!values.date) {
        toast.error("Please select start date");
        return;
      }
      if (!values.dueDate) {
        toast.error("Please select due date");
        return;
      }
      if (!values.rentDuration) {
        toast.error("Please enter rent duration");
        return;
      }
      if (!values.endDate) {
        toast.error("Please select end date");
        return;
      }

      const payload = {
        amount: Number(values.amount),
        date: moment(values.date).format("YYYY-MM-DD"),
        dueDate: moment(values.dueDate).format("YYYY-MM-DD"),
        endDate: values.endDate
          ? moment(values.endDate).format("YYYY-MM-DD")
          : null,
        description: values.description,
        rentDuration: values.rentDuration,
      };

      console.log("Advance Rent Payload Data:", payload);
      setIsAddDuesModal(false);
      toast.success("Dues Added Successfully");
    },
  });

  // 🔑 Auto-calc endDate & dueDate when rentDuration or startDate changes
  useEffect(() => {
    if (formik.values.rentDuration && formik.values.date) {
      const start = moment(formik.values.date);
      const months = parseInt(formik.values.rentDuration);

      const newEndDate = start
        .clone()
        .add(months, "months")
        .format("YYYY-MM-DD");
      const newDueDate = start
        .clone()
        .add(months, "months")
        .format("YYYY-MM-DD");

      formik.setFieldValue("endDate", newEndDate);
      formik.setFieldValue("dueDate", newDueDate);
    }
  }, [formik.values.rentDuration, formik.values.date]);

  return (
    <Container>
      <form className="px-1 flex flex-col gap-4" onSubmit={formik.handleSubmit}>
        <ScrollableContent className="!max-h-[calc(100vh-140px)]">
          <Wrapper>
            {/* Rent Duration */}
            <InputGroup>
              <Label htmlFor="rentDuration">Rent Duration *</Label>
              <Select
                {...formik.getFieldProps("rentDuration")}
                id="rentDuration"
                displayEmpty
                className="w-full px-2"
                placeholder="Select Rent Duration"
                style={{ height: "40px" }}
              >
                <MenuItem value="" disabled>
                  <Label>Select Rent Duration</Label>
                </MenuItem>
                {Array.from({ length: 11 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {i + 1} Month{i + 1 > 1 ? "s" : ""}
                  </MenuItem>
                ))}
              </Select>
            </InputGroup>
            {/* Amount */}
            <InputGroup>
              <Label htmlFor="amount">Amount *</Label>
              <FieldWrappers>
                <RupeeIcon />
                <input
                  type="text"
                  id="amount"
                  placeholder="Enter Amount"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  {...formik.getFieldProps("amount")}
                  onKeyDown={(e) => {
                    if (!/^\d$/.test(e.key) && e.key !== "Backspace") {
                      e.preventDefault();
                    }
                  }}
                  className="pl-6"
                />
              </FieldWrappers>
            </InputGroup>
            {/* Start Date & End Date */}
            <CombineInputGroup>
              <InputGroup>
                <Label htmlFor="date">Start Date *</Label>
                <FieldWrappers>
                  <CalendarIcon />
                  <input
                    type="date"
                    id="date"
                    {...formik.getFieldProps("date")}
                    className="pl-6 bg-gray-100"
                    disabled // 🔒 make Start Date read-only
                    placeholder="Start Date"
                  />
                </FieldWrappers>
              </InputGroup>

              <InputGroup>
                <Label htmlFor="endDate">End Date *</Label>
                <FieldWrappers>
                  <CalendarIcon />
                  <input
                    type="date"
                    id="endDate"
                    {...formik.getFieldProps("endDate")}
                    className="pl-5 bg-gray-100"
                    disabled // 🔒 make End Date read-only
                    placeholder="End Date"
                  />
                </FieldWrappers>
              </InputGroup>
            </CombineInputGroup>

            {/* Due Date */}
            <CombineInputGroup>
              <InputGroup>
                <Label htmlFor="dueDate">Due Date *</Label>
                <FieldWrappers>
                  <CalendarIcon />
                  <input
                    type="date"
                    id="dueDate"
                    {...formik.getFieldProps("dueDate")}
                    className="pl-6 bg-gray-100"
                    placeholder="Due Date"
                  />
                </FieldWrappers>
              </InputGroup>
            </CombineInputGroup>

            {/* Description */}
            <InputGroup>
              <Label htmlFor="description">Description *</Label>
              <FieldWrapper>
                <Textarea
                  id="description"
                  placeholder="Enter Description"
                  {...formik.getFieldProps("description")}
                  rows={4}
               />
              </FieldWrapper>
            </InputGroup>
          </Wrapper>
        </ScrollableContent>

        {/* Action Buttons */}
        <ActionSection>
          <SideCancelBtn type="button" onClick={() => setSelectedOption(null)}>
            Back
          </SideCancelBtn>
          <SubmitBtn type="submit">Add Dues</SubmitBtn>
        </ActionSection>
      </form>
    </Container>
  );
};
// Styled Components
const FieldWrapper = tw.div`w-full`;
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Textarea = tw.textarea`min-h-30 border rounded pl-2 py-2 w-full h-30 resize-y  break-words overflow-hidden`;
const CombineInputGroup = tw.div`w-full flex md:flex-row flex-col space-x-4 items-end`;
const InputGroup = tw.div`w-full flex flex-col space-y-1.5 mt-2 md:mt-0`;
const FieldWrappers = tw.div`pl-2 relative rounded-md w-full flex items-center border border-gray-300 h-10`;
const Label = tw.label`text-sm text-gray-500 font-normal`;
const SubmitBtn = tw.button`text-sm w-32 md:w-44 h-12 grid place-items-center disabled:opacity-50 disabled:cursor-not-allowed md:text-base bg-primary-color hover:bg-hover-color text-white rounded-md shadow-md`;
const SideCancelBtn = tw.button`text-sm w-32 md:w-44 h-12 grid place-items-center disabled:opacity-50 disabled:cursor-not-allowed md:text-base text-gray-600`;
const Wrapper = tw.div`grid gap-4 mt-4 overflow-y-auto`;
const ScrollableContent = tw.div`flex-grow overflow-y-auto max-h-[calc(100vh-200px)] custom-scrollbar mb-10`;
const ActionSection = tw.div`px-4 py-2 bg-white flex justify-between border-t border-gray-200 absolute bottom-0 left-0 w-full`;
const RupeeIcon = tw(FaRupeeSign)`
  absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3
`; const CalendarIcon = tw(FaRegCalendar)`
absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3
`;

export default AdvanceRent;
